import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from '../../hooks/useTranslation';
import SaleDataService from '../../services/sale.service';
import ProductDataService from '../../services/product.service';
import LocationDataService from '../../services/location.service';
import ClientDataService from '../../services/client.service';
import OriginDataService from '../../services/origin.service';
import InventoryDataService from '../../services/inventory.service';
import { formatCurrencySync, initializeCurrency } from '../../utils/currencyUtils';

const SaleForm = () => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [saleItems, setSaleItems] = useState([
    {
      id: Date.now(),
      product_id: '',
      quantity: '',
      unit_price: '',
      availableInventory: null,
    }
  ]);
  const [saleHeader, setSaleHeader] = useState({
    client_id: '',
    location_id: '',
    origin_id: '',
    sale_date: dayjs(),
    notes: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t, language } = useTranslation();

  useEffect(() => {
    fetchProducts();
    fetchLocations();
    fetchClients();
    fetchOrigins();
    initializeCurrency();
    if (isEdit) {
      fetchSale();
    }
  }, [id, isEdit]);

  useEffect(() => {
    // Fetch available inventory for each item when location changes
    saleItems.forEach((item, index) => {
      if (item.product_id && saleHeader.location_id) {
        fetchAvailableInventoryForItem(index, item.product_id, saleHeader.location_id);
      } else {
        updateItemAvailableInventory(index, null);
      }
    });
  }, [saleHeader.location_id]);

  const fetchProducts = async () => {
    try {
      const response = await ProductDataService.getAll();
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await LocationDataService.getAll();
      setLocations(response.data?.data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await ClientDataService.getClients();
      setClients(response.data?.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
  };

  const fetchOrigins = async () => {
    try {
      const response = await OriginDataService.getActive();
      setOrigins(response.data?.data || []);
    } catch (error) {
      console.error('Error loading origins:', error);
      setOrigins([]);
    }
  };

  const fetchAvailableInventoryForItem = async (itemIndex, productId, locationId) => {
    if (!productId || !locationId) {
      updateItemAvailableInventory(itemIndex, null);
      return;
    }

    try {
      const response = await InventoryDataService.getAll({ 
        product_id: productId,
        location_id: locationId 
      });
      const inventories = response.data?.data?.data || [];
      
      // Sum remaining_inventory from all buy transactions at this location
      const available = inventories
        .filter(inv => 
          inv.type === 'buy' && 
          inv.remaining_inventory !== undefined &&
          inv.location_id === parseInt(locationId)
        )
        .reduce((sum, inv) => sum + parseFloat(inv.remaining_inventory || 0), 0);
      
      updateItemAvailableInventory(itemIndex, available);
    } catch (error) {
      console.error('Error fetching available inventory:', error);
      updateItemAvailableInventory(itemIndex, null);
    }
  };

  const updateItemAvailableInventory = (itemIndex, available) => {
    setSaleItems(prev => prev.map((item, idx) => 
      idx === itemIndex ? { ...item, availableInventory: available } : item
    ));
  };

  const fetchSale = async () => {
    setLoading(true);
    try {
      const response = await SaleDataService.get(id);
      const sale = response.data?.data;
      setSaleHeader({
        client_id: sale.client_id || '',
        location_id: sale.location_id || '',
        origin_id: sale.origin_id || '',
        sale_date: sale.sale_date ? dayjs(sale.sale_date) : dayjs(sale.created_at),
        notes: sale.notes || '',
      });
      setSaleItems([{
        id: Date.now(),
        product_id: sale.product_id || '',
        quantity: sale.quantity || '',
        unit_price: sale.unit_price || '',
        availableInventory: null,
      }]);
    } catch (error) {
      console.error('Error loading sale:', error);
      showSnackbar(t('sales.errorLoadingSale'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setSaleHeader(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (itemId, field, value) => {
    setSaleItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        // If product changed, fetch available inventory and set price
        if (field === 'product_id' && value) {
          const itemIndex = prev.findIndex(i => i.id === itemId);
          const selectedProduct = products.find(p => p.id === parseInt(value));
          
          // Set unit price from product
          if (selectedProduct && selectedProduct.price) {
            updatedItem.unit_price = parseFloat(selectedProduct.price).toFixed(2);
          }
          
          // Fetch available inventory if location is selected
          if (saleHeader.location_id) {
            fetchAvailableInventoryForItem(itemIndex, value, saleHeader.location_id);
          }
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const addSaleItem = () => {
    setSaleItems(prev => [...prev, {
      id: Date.now(),
      product_id: '',
      quantity: '',
      unit_price: '',
      availableInventory: null,
    }]);
  };

  const removeSaleItem = (itemId) => {
    if (saleItems.length > 1) {
      setSaleItems(prev => prev.filter(item => item.id !== itemId));
    }
  };

  const handleSubmit = async () => {
    // Validate header
    if (!saleHeader.location_id) {
      showSnackbar(t('sales.pleaseSelectLocation'), 'error');
      return;
    }

    // Validate items
    const validItems = saleItems.filter(item => 
      item.product_id && item.quantity && item.unit_price
    );

    if (validItems.length === 0) {
      showSnackbar(t('sales.pleaseAddProduct'), 'error');
      return;
    }

    // Check for validation errors in items
    for (const item of validItems) {
      if (item.availableInventory !== null && parseFloat(item.quantity || 0) > item.availableInventory) {
        const product = products.find(p => p.id === parseInt(item.product_id));
        showSnackbar(
          `Insufficient inventory for ${product?.name || 'product'}. Available: ${item.availableInventory.toFixed(2)}, Requested: ${parseFloat(item.quantity).toFixed(2)}`,
          'error'
        );
        return;
      }
    }

    try {
      setLoading(true);
      
      // Create multiple sales (one per item)
      const salesPromises = validItems.map(item => {
        const submitData = {
          product_id: item.product_id,
          client_id: saleHeader.client_id || null,
          location_id: saleHeader.location_id,
          origin_id: saleHeader.origin_id || null,
          quantity: item.quantity,
          unit_price: item.unit_price,
          sale_date: saleHeader.sale_date ? saleHeader.sale_date.format('YYYY-MM-DD') : null,
          notes: saleHeader.notes,
        };
        return SaleDataService.create(submitData);
      });

      await Promise.all(salesPromises);
      showSnackbar(t('sales.saleRegisteredSuccess', { count: validItems.length }));
      navigate('/sales');
    } catch (error) {
      // Handle validation errors with available inventory info
      if (error.response?.status === 422 && error.response?.data?.available_inventory !== undefined) {
        const available = error.response.data.available_inventory;
        const requested = error.response.data.requested_quantity;
        showSnackbar(
          `${t('inventories.InsufficientInventory')}. ${t('inventories.AvailableInventory')}: ${available.toFixed(2)}, ${t('inventories.Quantity')}: ${requested.toFixed(2)}`,
          'error'
        );
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Error creating sale';
        showSnackbar(errorMessage, 'error');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getItemTotal = (item) => {
    if (item.quantity && item.unit_price) {
      return parseFloat(item.quantity) * parseFloat(item.unit_price);
    }
    return 0;
  };

  const getGrandTotal = () => {
    return saleItems.reduce((sum, item) => sum + getItemTotal(item), 0);
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => navigate('/sales')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">
                {isEdit ? t('sales.editSale') : t('sales.newSale')}
              </Typography>
            </Box>
          }
          action={
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={loading}
            >
              {t('common.register')} {t('sales.title')}
            </Button>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            {/* Header Fields */}
            <Grid item xs={12} md={6}>
              <DatePicker
                label={t('sales.date')}
                value={saleHeader.sale_date}
                onChange={(newValue) => setSaleHeader(prev => ({ ...prev, sale_date: newValue }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Client (Optional)</InputLabel>
                <Select
                  name="client_id"
                  value={saleHeader.client_id}
                  onChange={handleHeaderChange}
                  label="Client (Optional)"
                >
                  <MenuItem value="">No Client</MenuItem>
                  {clients.map(client => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Location *</InputLabel>
                <Select
                  name="location_id"
                  value={saleHeader.location_id}
                  onChange={handleHeaderChange}
                  label="Location *"
                  required
                >
                  <MenuItem value="">Select Location</MenuItem>
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Origin</InputLabel>
                <Select
                  name="origin_id"
                  value={saleHeader.origin_id}
                  onChange={handleHeaderChange}
                  label="Origin"
                >
                  <MenuItem value="">No Origin</MenuItem>
                  {origins.map(origin => (
                    <MenuItem key={origin.id} value={origin.id}>
                      {origin.name || (language === 'es' ? origin.name_es : origin.name_en)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Sale Items</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addSaleItem}
                  size="small"
                >
                  Add Item
                </Button>
              </Box>
            </Grid>

            {/* Sale Items Table */}
            <Grid item xs={12}>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="5%">#</TableCell>
                      <TableCell width="25%">Product *</TableCell>
                      <TableCell width="15%">Quantity *</TableCell>
                      <TableCell width="15%">Unit Price *</TableCell>
                      <TableCell width="15%">Total</TableCell>
                      <TableCell width="20%">Available</TableCell>
                      <TableCell width="5%" align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {saleItems.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={item.product_id}
                              onChange={(e) => handleItemChange(item.id, 'product_id', e.target.value)}
                              displayEmpty
                            >
                              <MenuItem value="">Select Product</MenuItem>
                              {products.map(product => (
                                <MenuItem key={product.id} value={product.id}>
                                  {product.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                            inputProps={{ step: 0.01, min: 0.01 }}
                            error={item.availableInventory !== null && parseFloat(item.quantity || 0) > item.availableInventory}
                            helperText={
                              item.availableInventory !== null
                                ? `Available: ${item.availableInventory.toFixed(2)}`
                                : ''
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => handleItemChange(item.id, 'unit_price', e.target.value)}
                            inputProps={{ step: 0.01, min: 0 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrencySync(getItemTotal(item))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.availableInventory !== null ? (
                            <Typography 
                              variant="body2" 
                              color={parseFloat(item.quantity || 0) > item.availableInventory ? 'error' : 'text.secondary'}
                            >
                              {item.availableInventory.toFixed(2)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Select product & location
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeSaleItem(item.id)}
                            disabled={saleItems.length === 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Grand Total */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                  Grand Total:
                </Typography>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  {formatCurrencySync(getGrandTotal())}
                </Typography>
              </Box>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('sales.notes')}
                name="notes"
                value={saleHeader.notes}
                onChange={handleHeaderChange}
                multiline
                rows={3}
                placeholder={t('sales.additionalNotes')}
              />
            </Grid>
          </Grid>
        </CardContent>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Card>
    </LocalizationProvider>
  );
};

export default SaleForm;
